import React, { useEffect, useState } from 'react'
import { AnimatedLogo, Button, createStyles, withStyles, Typography } from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme } from '@paintscout/ui'

export interface AppLoadingProps extends WithStyles<typeof styles> {
  /**
   * When true, if this loading screen is still mounted after `recoveryDelay` ms, show recovery
   * actions (reload / clear app data). Guards against startup hangs — e.g. WebKit's IndexedDB can
   * hang on `open()` after an iOS update, leaving the app stuck on this spinner forever.
   */
  withRecovery?: boolean
  /** How long to wait before showing recovery actions (ms). */
  recoveryDelay?: number
}

export const styles = (theme: Theme) =>
  createStyles({
    root: {
      height: '75vh',
      color: '#757575',
      width: '100vw',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    spinner: {
      marginBottom: 40
    },
    loading: {
      marginTop: 10,
      color: theme.paintscout.palette.brand.Neutral.P30
    },
    recovery: {
      marginTop: 24,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      maxWidth: 360,
      textAlign: 'center'
    },
    recoveryActions: {
      display: 'flex',
      gap: 12
    }
  })

/**
 * Best-effort wipe of all client-side storage for this origin, then reload. Each step is wrapped
 * so a hung IndexedDB (the very thing this recovers from) can't block the reload — `withTimeout`
 * ensures we always fall through to `window.location.reload()`.
 */
async function clearAppDataAndReload() {
  const withTimeout = <T,>(promise: Promise<T>, ms: number) =>
    Promise.race([promise, new Promise<void>((resolve) => setTimeout(resolve, ms))])

  try {
    if ('serviceWorker' in navigator) {
      await withTimeout(
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) => Promise.all(registrations.map((r) => r.unregister()))),
        3000
      )
    }
    if (typeof caches !== 'undefined') {
      await withTimeout(
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))),
        3000
      )
    }
    if (window.indexedDB?.databases) {
      const deleteDatabase = (name: string) =>
        new Promise<void>((resolve) => {
          const req = window.indexedDB.deleteDatabase(name)
          // resolve on any terminal state — a blocked/errored delete shouldn't hold up the reload
          req.onsuccess = req.onerror = req.onblocked = () => resolve()
        })
      await withTimeout(
        window.indexedDB
          .databases()
          .then((dbs) => Promise.all(dbs.map((db) => (db.name ? deleteDatabase(db.name) : Promise.resolve())))),
        3000
      )
    }
  } catch {
    // best-effort — reload regardless
  } finally {
    window.location.reload()
  }
}

function AppLoading(props: AppLoadingProps) {
  const { classes, withRecovery, recoveryDelay = 15000 } = props
  const [showRecovery, setShowRecovery] = useState(false)

  useEffect(() => {
    if (!withRecovery) return
    const timer = setTimeout(() => setShowRecovery(true), recoveryDelay)
    return () => clearTimeout(timer)
  }, [withRecovery, recoveryDelay])

  return (
    <div className={classes.root} data-testid="app-loading">
      <AnimatedLogo scale={0.21} />
      <Typography variant="overline" classes={{ root: classes.loading }}>
        &nbsp;&nbsp;Loading...
      </Typography>
      {showRecovery && (
        <div className={classes.recovery} data-testid="app-loading-recovery">
          <Typography variant="body2">
            Still loading? Your saved data on this device may be out of date. Try reloading, or clear this device&apos;s
            app data to start fresh (you won&apos;t lose any quotes — they&apos;re saved online).
          </Typography>
          <div className={classes.recoveryActions}>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button variant="contained" color="primary" onClick={clearAppDataAndReload}>
              Clear app data &amp; reload
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
export default withStyles(styles)(AppLoading)
